import {
    addDays,
    addHours,
    format,
    Interval,
    isBefore,
    isDate,
    isSameYear,
    isToday,
    isTomorrow,
    isYesterday,
    subDays,
    subHours,
} from "date-fns";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import { nl } from "date-fns/locale";

const TZ_EUROPE_AMSTERDAM = "Europe/Amsterdam";

type WithReplacedDates<T, R> = T extends string
    ? R
    : T extends (infer U)[]
      ? WithReplacedDates<U, R>[]
      : T extends object
        ? { [K in keyof T]: WithReplacedDates<T[K], R> }
        : T;

export function replaceIsoStrings<T, R = Date>(value: T, replacer?: (date: Date) => R): WithReplacedDates<T, R> {
    if (typeof value === "string" && isIsoDateString(value)) {
        const date = new Date(value);
        return (replacer ? replacer(date) : date) as WithReplacedDates<T, R>;
    }

    if (Array.isArray(value)) {
        return value.map((v) => replaceIsoStrings(v as WithReplacedDates<T, R>, replacer)) as WithReplacedDates<T, R>;
    }

    if (value !== null && typeof value === "object") {
        const result = {} as { [K in keyof T]: WithReplacedDates<T[K], R> };

        for (const key in value) {
            result[key] = replaceIsoStrings(value[key], replacer);
        }

        return result as WithReplacedDates<T, R>;
    }

    return value as WithReplacedDates<T, R>;
}

function isIsoDateString(value: string): boolean {
    return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/.test(value) && !Number.isNaN(Date.parse(value));
}

export function hourFormatInAmsterdam(dateTime: Date): string {
    return formatInTimeZone(dateTime, TZ_EUROPE_AMSTERDAM, "HH:mm");
}

export function formatDateRelative(date: Date) {
    if (!date || isNaN(date.getTime())) return "";
    if (isToday(date)) {
        return "Vandaag";
    } else if (isYesterday(date)) {
        return "Gisteren";
    } else if (isTomorrow(date)) {
        return "Morgen";
    } else if (isSameYear(date, new Date())) {
        // trim the trailing "." at the end of the months.
        return format(date, "d MMM", { locale: nl }).replace(/(\w\w\w)\./, "$1");
    }
    // trim the trailing "." at the end of the months.
    return format(date, "dd MMM yy", { locale: nl }).replace(/(\w\w\w)\./, "$1");
}

export function formatDateTimeRelative(date: Date) {
    if (!date || isNaN(date.getTime())) return "";
    if (isToday(date)) {
        return format(date, "HH:mm", { locale: nl });
    } else if (isYesterday(date)) {
        return `${format(date, "HH:mm", { locale: nl })}, gisteren`;
    } else if (isTomorrow(date)) {
        return `${format(date, "HH:mm", { locale: nl })}, morgen`;
    } else if (isSameYear(date, new Date())) {
        // trim the trailing "." at the end of the months.
        return format(date, "HH:mm, dd MMM", { locale: nl }).replace(/(\w\w\w)\./, "$1");
    }
    // trim the trailing "." at the end of the months.
    return format(date, "HH:mm, dd MMM yy", { locale: nl }).replace(/(\w\w\w)\./, "$1");
}

export type Time = `${number}${number}:${number}${number}`;
const timeRegex = /^(([01]\d|2[0-3]):[0-5]\d)$/;

export function createInterval(startTime: Time, endTime: Time, timeZone = TZ_EUROPE_AMSTERDAM): Interval<Date, Date> {
    if (!timeRegex.test(startTime) || !timeRegex.test(endTime)) throw new Error("Invalid time format.");

    const now = new Date();
    const date = formatInTimeZone(now, timeZone, "yyyy-MM-dd");

    const start = fromZonedTime(`${date} ${startTime}`, timeZone);
    const end = fromZonedTime(`${date} ${endTime}`, timeZone);

    return { start, end };
}

type DateKeys<T> = {
    [K in keyof T]: T[K] extends Date ? K : never;
}[keyof T];

export function mostRecent<T, K extends DateKeys<T>>(items: T[], dateKey: K): T | null {
    if (!items.length) return null;

    const getTimestamp = (item: T): number => (item[dateKey] as Date).getTime();

    return items.reduce((mostRecentSoFar, current) => (getTimestamp(current) > getTimestamp(mostRecentSoFar) ? current : mostRecentSoFar));
}

export function timeToDate(time: Time): Date {
    const [hours, minutes] = time.split(":").map(Number);

    const now = new Date();
    const windowStart = subHours(now, 4);
    const windowEnd = addHours(now, 20);

    const today = new Date(now);
    today.setHours(hours, minutes, 0, 0);

    if (today >= windowStart && today <= windowEnd) {
        return fromZonedTime(today, TZ_EUROPE_AMSTERDAM);
    }

    if (today < windowStart) {
        // More than 4 hours in the past → must be tomorrow
        const tomorrow = addDays(today, 1);
        return fromZonedTime(tomorrow, TZ_EUROPE_AMSTERDAM);
    }

    // More than 20 hours in the future → must be yesterday
    const yesterday = subDays(today, 1);
    return fromZonedTime(yesterday, TZ_EUROPE_AMSTERDAM);
}

export const extractTimeAndDate = (value: unknown): [string | undefined, Date | undefined] => {
    if (!isDate(value)) return [undefined, undefined];
    const time = hourFormatInAmsterdam(value);
    return [time, value];
};

export function timeTodayOrTomorrow(time: Time) {
    const [hours, minutes] = time.split(":").map(Number);
    const now = new Date();
    const today = new Date(now);
    today.setHours(hours, minutes, 0, 0);
    if (isBefore(today, now)) {
        return addDays(today, 1); // this time isn't after Date.now(), so we'll assume it's supposed to be tomorrow
    }
    return today;
}
